/**
 * Status-bar height as a fraction of the screen, measured on the device.
 *
 * The OCR engine never reads the top band of a captured frame: the system
 * status bar (clock, signal, battery) lives there and its text is noise.
 * That band used to be one number per platform (6% on iOS, 4% on Android),
 * sized for the Dynamic Island of the phone the scanner was calibrated on.
 * A device with a shorter bar then wastes the top of every frame: an iPad's
 * bar is ~2% of its height, an SE-class iPhone's ~3%, and GO's CP line,
 * which starts right under the bar, can lose the tops of its digits (the
 * Android 4% exists because 6% did exactly that on a Galaxy S21+).
 *
 * The app's own WebView fills the screen (Capacitor contentInset "never",
 * Android edge-to-edge), so var(--nx-safe-top) IS this device's status-bar
 * band in CSS px, and the screen height in CSS px is the height that band
 * sits in. The fraction carries straight over to a broadcast (iOS) or
 * MediaProjection (Android) frame: both capture the whole screen at one
 * uniform scale, so fractions of height survive the capture.
 *
 * The measurement only ever LOWERS the exclusion (see
 * resolveStatusBarExclusion): the platform default stays the ceiling, so the
 * phones the scanner was calibrated on read exactly what they read today, and
 * a device whose bar is taller than the default is never clamped further.
 *
 * Pure parts (resolveStatusBarExclusion) are pinned by statusBarInset.test.ts.
 */

/**
 * Air left between the measured bar and the first row the engine reads, as a
 * fraction of frame height. ~5 px on an 844 pt phone, ~6 px on an iPad.
 */
export const STATUS_BAR_MARGIN = 0.006;

/**
 * A measurement within this much of the platform default keeps the default.
 * The defaults were tuned on device frames, so a hair-lower measurement is
 * not worth moving a calibrated clamp for (the Galaxy S21+ measures ~0.035;
 * with the margin that is 0.041, which stays on the 0.04 Android default).
 */
export const STATUS_BAR_HYSTERESIS = 0.01;

/** Never read closer than this to the top edge, whatever the bar measures. */
export const STATUS_BAR_FLOOR = 0.015;

/**
 * The fraction of the frame the engine should skip at the top, given the
 * platform's calibrated default and the measured status-bar fraction (null
 * when nothing could be measured). Monotone: never above the default.
 */
export function resolveStatusBarExclusion(
  platformDefault: number,
  measuredFrac: number | null,
): number {
  if (measuredFrac == null || !Number.isFinite(measuredFrac) || measuredFrac < 0) {
    return platformDefault;
  }
  const candidate = Math.max(STATUS_BAR_FLOOR, measuredFrac + STATUS_BAR_MARGIN);
  if (candidate >= platformDefault - STATUS_BAR_HYSTERESIS) return platformDefault;
  return candidate;
}

/**
 * Resolve var(--nx-safe-top) to CSS px by applying it as padding on a probe
 * element: getComputedStyle cannot evaluate the max(env(), var()) expression
 * through getPropertyValue, but it does resolve a used padding value.
 */
function readSafeTopPx(): number | null {
  if (typeof document === "undefined" || typeof getComputedStyle !== "function") return null;
  const body = document.body;
  if (!body) return null;
  const probe = document.createElement("div");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText =
    "position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;" +
    "padding-top:var(--nx-safe-top, 0px)";
  body.appendChild(probe);
  try {
    const px = parseFloat(getComputedStyle(probe).paddingTop);
    return Number.isFinite(px) ? px : null;
  } finally {
    probe.remove();
  }
}

/** Screen height in CSS px: the full screen, not the (keyboard-shrunk) viewport. */
function screenHeightPx(): number | null {
  if (typeof window === "undefined") return null;
  const h = window.screen?.height || window.innerHeight;
  return h > 0 ? h : null;
}

/** One measurement of the status-bar band as a fraction of the screen height. */
export function measureStatusBarFraction(): number | null {
  const px = readSafeTopPx();
  const h = screenHeightPx();
  if (px == null || h == null) return null;
  const frac = px / h;
  // A whole-screen bar (or a probe that failed to resolve) is not a status bar.
  if (!Number.isFinite(frac) || frac < 0 || frac > 0.25) return null;
  return frac;
}

let cached: number | null | undefined;
let listening = false;

/**
 * The measured status-bar fraction, cached and refreshed when the window
 * resizes (a rotation, a split-view change). Null when it cannot be measured,
 * which the engine treats as "use the platform default".
 */
export function getStatusBarFraction(): number | null {
  if (cached === undefined) cached = measureStatusBarFraction();
  if (!listening && typeof window !== "undefined") {
    listening = true;
    window.addEventListener("resize", () => {
      cached = measureStatusBarFraction();
    });
  }
  return cached;
}

/** Test seam: forget the cached measurement. */
export function resetStatusBarFractionForTests(): void {
  cached = undefined;
}
